<template>
  <div class="login-overlay" @click.self="$emit('close')">
    <div class="login-card">

      <div class="login-header">
        <h2>{{ t('newChatTitle') }}</h2>
        <button class="close-btn" @click="$emit('close')">&#10005;</button>
      </div>

      <div class="form">

        <div class="info-message">{{ t('newChatExplain') }}</div>

        <input
            v-model="firstName"
            type="text"
            :placeholder="t('firstName')"
            autocomplete="given-name"
        />

        <input
            v-model="lastName"
            type="text"
            :placeholder="t('lastName')"
            autocomplete="family-name"
        />

        <input
            v-model="email"
            type="email"
            :placeholder="t('email')"
            autocomplete="email"
        />

        <textarea
            v-model="message"
            :placeholder="t('accountCreationMessage')"
            rows="3"
        />

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
        <div v-if="successMessage" class="success-message">{{ successMessage }}</div>

        <button :disabled="!canSubmit || submitting" @click="submit">
          {{ t('create') }}
        </button>

        <button class="secondary-btn" @click="$emit('close')">
          {{ t('cancel') }}
        </button>

      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "../i18n.js"

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

export default {
  name: "ChatNewClient",

  props: {
    service: { type: Object, required: true }
  },

  emits: ["created", "close"],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      message: "",
      submitting: false,
      errorMessage: "",
      successMessage: ""
    }
  },

  computed: {
    canSubmit() {
      return this.firstName.trim().length > 0
          && this.lastName.trim().length > 0
          && EMAIL_REGEX.test(this.email.trim())
    }
  },

  methods: {
    async submit() {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.errorMessage = ""
      this.successMessage = ""
      try {
        await this.service.addClient({
          firstName: this.firstName.trim(),
          lastName: this.lastName.trim(),
          email: this.email.trim(),
          message: this.message.trim()
        })
        this.successMessage = this.t('newChatSuccess')
        // Brief delay so the user sees the confirmation, then close + refresh
        setTimeout(() => this.$emit('created'), 1200)
      } catch (e) {
        this.errorMessage = this.t('newChatFailed')
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.login-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.login-header h2 { margin: 0; font-size: 18px; }

.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}
.close-btn:hover { color: #000; }

.login-card {
  width: min(460px, calc(100vw - 32px));
  background: white;
  padding: 24px;
  border-radius: 12px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

input, textarea {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-family: inherit;
  font-size: 16px;
}

textarea {
  resize: vertical;
  line-height: 1.4;
}

button {
  padding: 12px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
}

button:disabled {
  background: #c7c9d1;
  cursor: not-allowed;
}

.secondary-btn {
  background: #f0f2f5;
  color: #1a1a2e;
}

.secondary-btn:hover { background: #e4e8ee; }

.info-message {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.success-message {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}
</style>
